// frontend/src/screens/user/MySwapRequestsScreen.jsx
import React, { useState } from 'react';
import { useLayout } from '../context/LayoutContext';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Breadcrumbs,
  Link,
  Snackbar,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { NavigateNext, SwapHoriz, CheckCircle, Cancel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  useGetMySwapRequestsQuery,
  useAcceptSwapRequestMutation,
  useGetMyAcceptedSwapRequestsQuery,
} from '../slices/swapApiSlice';

const formatDateTime = (d) =>
  new Date(d).toLocaleString('en-IE', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const STATUS_CHIP = {
  PendingApproval: { label: 'Awaiting manager approval', color: 'warning' },
  Approved:        { label: 'Approved', color: 'success' },
  Rejected:        { label: 'Rejected', color: 'error' },
};

const OfferedToYouTab = ({ isMobile, showSnackbar }) => {
  const [dismissedIds, setDismissedIds] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, swapRequest: null });

  const {
    data: swapRequests,
    isLoading,
    error,
    refetch,
  } = useGetMySwapRequestsQuery(undefined, {
    pollingInterval: 60000,
  });

  const [acceptSwapRequest, { isLoading: accepting }] = useAcceptSwapRequestMutation();

  const visibleRequests = (swapRequests || []).filter((r) => !dismissedIds.includes(r._id));

  const handleDismiss = (id) => setDismissedIds((prev) => [...prev, id]);

  const handleAcceptClick = (swapRequest) => setConfirmDialog({ open: true, swapRequest });

  const handleConfirmAccept = async () => {
    const swapRequest = confirmDialog.swapRequest;
    setConfirmDialog({ open: false, swapRequest: null });
    try {
      await acceptSwapRequest(swapRequest._id).unwrap();
      showSnackbar('Shift accepted — awaiting manager approval. Check "My Accepted Swaps" for status.', 'success');
    } catch (err) {
      showSnackbar(err?.data?.message || err?.error || 'Could not accept this shift', 'error');
      refetch();
    }
  };

  return (
    <>
      <Paper elevation={2} sx={{ p: isMobile ? 1.5 : 2 }}>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        )}

        {error && <Alert severity="error">Could not load swap requests. Please try again.</Alert>}

        {!isLoading && visibleRequests.length === 0 && (
          <Alert severity="info">No open swap requests at the moment.</Alert>
        )}

        <List>
          {visibleRequests.map((r, idx) => (
            <React.Fragment key={r._id}>
              {idx > 0 && <Divider component="li" />}
              <ListItem
                sx={{ py: 2 }}
                secondaryAction={
                  <Stack direction={isMobile ? 'column' : 'row'} spacing={1}>
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={<Cancel />}
                      onClick={() => handleDismiss(r._id)}
                      disabled={accepting}
                    >
                      Not Interested
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => handleAcceptClick(r)}
                      disabled={accepting}
                    >
                      Accept
                    </Button>
                  </Stack>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {formatDateTime(r.shift?.startDateTime)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        to {formatDateTime(r.shift?.endDateTime)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <>
                      <Box component="span" sx={{ display: 'block' }}>
                        {r.shift?.wardDepartmentArea}
                        {r.shift?.hospitalLocationName && r.shift.hospitalLocationName !== r.shift.wardDepartmentArea
                          ? ` · ${r.shift.hospitalLocationName}`
                          : ''}
                        {' · '}{r.shift?.categoryOfStaff}
                      </Box>
                      <Box component="span" sx={{ display: 'block' }}>
                        Requested by {r.requestingStaffName}
                        {r.reason && ` — "${r.reason}"`}
                      </Box>
                    </>
                  }
                  sx={{ pr: isMobile ? 0 : 20, mb: isMobile ? 1 : 0 }}
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Paper>

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, swapRequest: null })}>
        <DialogTitle>Accept this shift?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.swapRequest && (
              <>
                You're accepting the shift from{' '}
                <strong>{formatDateTime(confirmDialog.swapRequest.shift?.startDateTime)}</strong> to{' '}
                <strong>{formatDateTime(confirmDialog.swapRequest.shift?.endDateTime)}</strong> in{' '}
                <strong>{confirmDialog.swapRequest.shift?.wardDepartmentArea}</strong>
                {confirmDialog.swapRequest.shift?.hospitalLocationName
                  && confirmDialog.swapRequest.shift.hospitalLocationName !== confirmDialog.swapRequest.shift?.wardDepartmentArea
                  && <> ({confirmDialog.swapRequest.shift.hospitalLocationName})</>}
                , requested by {confirmDialog.swapRequest.requestingStaffName}.
                <br /><br />
                This is first-come, first-served — if someone else accepts first, you'll be notified.
                Once accepted, it still needs manager approval before it's final.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, swapRequest: null })}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleConfirmAccept} disabled={accepting}>
            {accepting ? 'Accepting…' : 'Confirm Accept'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const MyAcceptedTab = ({ isMobile }) => {
  const {
    data: acceptedRequests,
    isLoading,
    error,
  } = useGetMyAcceptedSwapRequestsQuery(undefined, {
    pollingInterval: 60000,
  });

  return (
    <Paper elevation={2} sx={{ p: isMobile ? 1.5 : 2 }}>
      {isLoading && (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      )}

      {error && <Alert severity="error">Could not load your accepted swaps.</Alert>}

      {!isLoading && acceptedRequests?.length === 0 && (
        <Alert severity="info">You haven't accepted any shift swaps yet.</Alert>
      )}

      <List>
        {acceptedRequests?.map((r, idx) => {
          const statusInfo = STATUS_CHIP[r.status] || { label: r.status, color: 'default' };

          return (
            <React.Fragment key={r._id}>
              {idx > 0 && <Divider component="li" />}
              <ListItem sx={{ py: 2 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {formatDateTime(r.shiftSnapshot?.startDateTime)}
                      <Chip label={statusInfo.label} size="small" color={statusInfo.color} sx={{ height: 20, fontSize: '0.7rem' }} />
                    </Box>
                  }
                  secondary={
                    <>
                      <Box component="span" sx={{ display: 'block' }}>
                        to {formatDateTime(r.shiftSnapshot?.endDateTime)}
                      </Box>
                      <Box component="span" sx={{ display: 'block' }}>
                        {r.shiftSnapshot?.wardDepartmentArea} · {r.shiftSnapshot?.categoryOfStaff}
                      </Box>
                      <Box component="span" sx={{ display: 'block' }}>
                        Swapped with {r.requestingStaffName}
                      </Box>
                      {r.managerDecision?.comment && (
                        <Box component="span" sx={{ display: 'block' }}>
                          Manager comment: "{r.managerDecision.comment}"
                        </Box>
                      )}
                    </>
                  }
                />
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
    </Paper>
  );
};

const MySwapRequestsScreen = () => {
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  React.useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  return (
    <Box sx={{ p: isMobile ? 1.5 : 3 }}>
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 1.5 }}>
        <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>
          Dashboard
        </Link>
        <Typography color="text.primary" variant={isMobile ? 'body2' : 'body1'}>
          Shift Swap Requests
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <SwapHoriz color="primary" />
        <Typography variant={isMobile ? 'h5' : 'h4'}>Shift Swap Requests</Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, val) => {
          if (val === 2) {
            // Cross-link tab — navigates to the other swap screen rather than
            // switching local content, since that screen owns its own data.
            navigate('/my-shifts/request-swap');
            return;
          }
          setActiveTab(val);
        }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Shifts Offered to You" />
        <Tab label="My Accepted Swaps" />
        <Tab label="Your Shift Requests" />
      </Tabs>

      {activeTab === 0 && <OfferedToYouTab isMobile={isMobile} showSnackbar={showSnackbar} />}
      {activeTab === 1 && <MyAcceptedTab isMobile={isMobile} />}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default MySwapRequestsScreen;